import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createMasterItem,
  updateMasterItem,
  addMasterItemPhoto,
  removeMasterItemPhoto,
} from '../../api/catalog';
import { listPlatformCategories } from '../../api/platformCategories';
import { useMasterItemPhotoUrl } from '../../hooks/useCatalogItemImageUrl';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import { PhotoGallery } from '../../components/PhotoGallery';
import type { MasterCatalogItem } from '../../types/api';

interface Props {
  open: boolean;
  onClose: () => void;
  item?: MasterCatalogItem | null;
}

export function MasterItemModal({ open, onClose, item }: Props) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isEdit = !!item;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [defaultCostMonthly, setDefaultCostMonthly] = useState('');
  const [defaultCostTotal, setDefaultCostTotal] = useState('');
  const [error, setError] = useState<string | null>(null);

  // §21: MSP-Admin-managed platform category list, replacing what used to
  // be a hardcoded constant here.
  const categoriesQuery = useQuery({
    queryKey: ['platform-categories'],
    queryFn: listPlatformCategories,
    enabled: open,
  });
  const categories = categoriesQuery.data ?? [];

  useEffect(() => {
    if (!open) return;
    setName(item?.name ?? '');
    setDescription(item?.description ?? '');
    setCategory(item?.category ?? '');
    setDefaultCostMonthly(item?.defaultCostMonthly ?? '');
    setDefaultCostTotal(item?.defaultCostTotal ?? '');
    setError(null);
  }, [open, item]);

  const addPhotoMutation = useMutation({
    mutationFn: (file: File) => addMasterItemPhoto(item!.id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['master-items'] });
      showToast('Photo added.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not upload the photo.')),
  });

  const removePhotoMutation = useMutation({
    mutationFn: (photoId: string) => removeMasterItemPhoto(item!.id, photoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['master-items'] });
      showToast('Photo removed.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not remove the photo.')),
  });

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        description: description || undefined,
        category: category || undefined,
        defaultCostMonthly: defaultCostMonthly ? Number(defaultCostMonthly) : undefined,
        defaultCostTotal: defaultCostTotal ? Number(defaultCostTotal) : undefined,
      };
      return isEdit ? updateMasterItem(item!.id, payload) : createMasterItem(payload);
    },
    onSuccess: (savedItem) => {
      queryClient.invalidateQueries({ queryKey: ['master-items'] });
      showToast(isEdit ? `${savedItem.name} updated.` : `${savedItem.name} added to the master catalog.`);
      if (!isEdit) {
        setName('');
        setDescription('');
        setCategory('');
        setDefaultCostMonthly('');
        setDefaultCostTotal('');
        onClose();
      }
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not save the master item.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit master catalog item' : 'New master catalog item'}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            {isEdit ? 'Close' : 'Cancel'}
          </Button>
          <Button form="master-item-form" type="submit" isLoading={mutation.isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="master-item-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input label="Name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        <Input label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Photos</p>
          {isEdit ? (
            <PhotoGallery
              photos={item!.photos}
              usePhotoUrl={(photoId) => useMasterItemPhotoUrl(item!.id, photoId)}
              onAdd={(file) => {
                setError(null);
                addPhotoMutation.mutate(file);
              }}
              onRemove={(photoId) => {
                setError(null);
                removePhotoMutation.mutate(photoId);
              }}
              isUploading={addPhotoMutation.isPending}
              isRemoving={removePhotoMutation.isPending}
            />
          ) : (
            <p className="text-xs text-slate-500">Save the item first, then edit it to add photos.</p>
          )}
        </div>

        <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">No category</option>
          {categories.map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
          {category && !categories.some((c) => c.name === category) && <option value={category}>{category}</option>}
        </Select>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Default monthly cost (£)"
            type="number"
            step="0.01"
            min="0"
            value={defaultCostMonthly}
            onChange={(e) => setDefaultCostMonthly(e.target.value)}
          />
          <Input
            label="Default total cost (£)"
            type="number"
            step="0.01"
            min="0"
            value={defaultCostTotal}
            onChange={(e) => setDefaultCostTotal(e.target.value)}
          />
        </div>
      </form>
    </Modal>
  );
}
